/* eslint-disable simple-import-sort/imports */
/* eslint-disable simple-import-sort/exports */
import { Delete, MusicNote } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoIcon from '@mui/icons-material/Photo';
import {
  Alert,
  Box,
  FormControl,
  FormLabel,
  LinearProgress,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import IconButton from '@mui/material/IconButton';
import { DocumenetUploadIcon } from 'components/Icons/DocumenetUploadIcon';
import { NewUploadIcon } from 'components/Icons/UploadIcon';
import { VideoUploadIcon } from 'components/Icons/VideoUploadIcon';
import { getLocalStorageItem } from 'hooks';
import { useCreateNewMessageQuery } from 'queries/Message';
import {
  useUserBlastGroupListApiQuery,
  useUserTableListApiQuery,
} from 'queries/Users';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SingleSelectComponent from 'components/CustomSingleSelect';

interface Props {
  open: boolean;
  onOpenChange: any;
}

export const InternalNewMessageModal = ({ open, onOpenChange }: Props) => {
  const [description, setDescription] = useState('');
  const userDetails = getLocalStorageItem('roles');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [options, setOptions] = useState<any>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState<any>(null);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const quillRef = useRef<ReactQuill | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [quillInstance, setQuillInstance] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [textLine, setTextLine] = useState('');
  const { t } = useTranslation('message');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (value: string) => {
    setDescription(value);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const { mutate: addNewRequest } = useCreateNewMessageQuery();
  const {
    data: userListApi,
    dataUpdatedAt,
    isLoading,
  } = useUserTableListApiQuery({
    userParams: {
      accountId: userDetails?.account?.id,
      active: true,
    },
  });
  const { data: userBlastGroup } = useUserBlastGroupListApiQuery(
    userDetails?.account?.id
  );

  const [files, setFiles] = useState<any>([]);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const newFiles = Array.from(e.target.files); // Convert FileList to Array
      setFiles((prevFiles: any[]) => [...prevFiles, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev: any) => prev.filter((_: any, i: any) => i !== index));
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) {
      const droppedFiles = Array.from(e.dataTransfer.files).map((file) => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 100, // Assume file is 'selected'
      }));
      setFiles((prevFiles: any) => [...prevFiles, ...droppedFiles]); // Add to existing files
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/'))
      return (
        <div
          className="rounded-full bg-primary-500/10 p-1"
          style={{ lineHeight: '0px' }}
        >
          <PhotoIcon color="primary" />
        </div>
      );
    if (fileType.startsWith('video/')) return <VideoUploadIcon />;
    if (fileType.startsWith('audio/')) return <MusicNote color="success" />;
    if (fileType.startsWith('application/pdf')) return <DocumenetUploadIcon />;
    return <DocumenetUploadIcon />;
  };
  const getFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`; // Bytes for very small files
    }
    if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(2)} KB`; // Kilobytes
    }
    return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`; // Megabytes
  };

  // Call your useSearchPatients hook
  const handleTextLine = (text: string) => {
    setTextLine(text);
  };

  const handleSave = () => {
    const formData = new FormData();
    if (files.length > 0) {
      files.forEach((file: any) => {
        formData.append('files', file);
      });
    }
    formData.append('title', textLine);
    formData.append('description', description);
    formData.append(
      'message_type ',
      selectedUserGroup?.group ? 'blast' : 'chat'
    );
    formData.append('patient_id', selectedUser.toString());
    formData.append('topic_id', '');
    formData.append(
      'created_by',
      `${userDetails?.firstName} ${userDetails?.lastName}`
    );
    addNewRequest(
      { formData },
      {
        onSuccess: () => {
          showSnackbar('Message created successfully!', 'success');
          onOpenChange(false);
        },
        onError: (error: any) => {
          showSnackbar(
            `Error: ${error?.message || 'Failed to create message'}`,
            'error'
          );
        },
      }
    );
    onOpenChange(false);
    setSearchQuery('');
    setFiles([]);
    setDescription('');
    setTextLine('');
  };

  const handleClose = () => {
    onOpenChange(false);
    setSearchQuery('');
    setFiles([]);
    setDescription('');
    setTextLine('');
  };

  useEffect(() => {
    if (quillRef.current && wrapperRef.current) {
      const toolbars = wrapperRef.current.querySelectorAll(
        '.ql-formats, .ql-picker-label, .ql-picker-item, .ql-bold, .ql-italic, .ql-underline, .ql-link, .ql-list, .ql-clean'
      );
      toolbars.forEach((toolbar) => {
        toolbar.setAttribute('tabindex', '-1');
      });
    }
  }, [quillInstance]);

  useEffect(() => {
    if (userListApi?.data || userBlastGroup) {
      const object: any = [];
      const blastObject: any = [];
      userListApi?.data?.forEach((item: any) => {
        object.push({
          id: item?.id,
          title: item?.username,
          image: item?.picture,
          name: item?.name,
        });
      });
      userBlastGroup?.forEach((item: any) => {
        blastObject.push({
          id: item?.id,
          title: item?.description,
          image: item?.picture,
          name: item?.name,
          group: item?.groupChannelId,
        });
      });
      setOptions([...object, ...blastObject]);
    }
  }, [userListApi?.data, userBlastGroup]);

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {open && (
        <section className="fixed left-0 top-0 z-40 flex h-[100vh] w-full justify-end overflow-hidden bg-[#0000000d]">
          <article className="flex h-[calc(100vh-100px)] w-[400px] flex-col justify-between overflow-auto rounded-xl rounded-r-none bg-white p-6 shadow-md md:h-[calc(100vh-48px)]">
            {/* Header Section */}
            <div className="flex flex-col gap-6">
              <div className="flex justify-between">
                <div>
                  <Typography variant="h6" className="font-bold">
                    {t('newMessage')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('Please fill out details to send a message.')}
                  </Typography>
                </div>
                <div className="">
                  <IconButton
                    aria-label="Close"
                    onClick={() => onOpenChange(false)}
                    className="text-black"
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>

              {/* Form Section */}
              <div>
                <FormControl className="flex flex-col gap-4">
                  {/* Patient Select */}
                  <div>
                    <FormLabel
                      htmlFor="Topic_Line"
                      className="mb-1 text-sm font-medium"
                    >
                      {t('Users')}
                    </FormLabel>
                    <SingleSelectComponent
                      data={options}
                      handleValue={(val: any) => {
                        setSelectedUser(val?.id);
                        setSelectedUserGroup(val);
                      }}
                      handleEventValue={(ele: any) => {
                        setSearchKeyword(ele);
                      }}
                    />
                  </div>
                  {/* Topic Line Select */}
                  <div>
                    <FormLabel
                      htmlFor="Topic_Line"
                      className="mb-1 text-sm font-medium"
                    >
                      {t('topicLine')}
                    </FormLabel>
                    <TextField
                      className="w-full rounded-md text-sm placeholder:text-gray-500"
                      onChange={(e: any) => {
                        handleTextLine(e.target.value);
                      }}
                      placeholder="Topic Line"
                      value={textLine}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px', // Rounded corners
                          // padding: '8px', // Padding similar to your styles
                          '& fieldset': {
                            borderColor: '#d1d5db', // Default border color (gray-300)
                          },
                          '&:hover fieldset': {
                            borderColor: '#9ca3af', // Border on hover (gray-400)
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#06aed4', // Focus ring color (primary-500)
                            borderWidth: '2px', // Focus ring thickness
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem', // Text size (text-sm)
                          color: '#374151', // Default text color (gray-700)
                          '&::placeholder': {
                            color: '#9ca3af', // Placeholder color (gray-500)
                          },
                        },
                      }}
                    />
                  </div>

                  {/* Description Textarea */}
                  <div>
                    <FormLabel
                      htmlFor="Description"
                      className="mb-1 text-sm font-medium"
                    >
                      {t('Message')}
                    </FormLabel>
                    <div ref={wrapperRef}>
                      <ReactQuill
                        ref={(el) => {
                          quillRef.current = el;
                          if (el) {
                            setQuillInstance(el);
                          }
                        }}
                        theme="snow"
                        value={description}
                        onChange={(value) => handleChange(value)}
                        modules={{
                          clipboard: {
                            matchVisual: true, // Ensures Quill processes pasted HTML properly
                          },
                        }}
                        placeholder="Enter a description..."
                        className="ql-formats w-full resize-none rounded-md border border-gray-300 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* File Upload Placeholder */}
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="file-upload"
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) =>
                        handleDrop(e as React.DragEvent<HTMLLabelElement>)
                      }
                      className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 ${
                        dragOver
                          ? 'border-solid border-primary-500'
                          : 'border-dashed border-gray-300'
                      } bg-white p-6`}
                    >
                      {/* Upload Icon */}
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                        <NewUploadIcon />
                      </div>

                      {/* Upload Text */}
                      <Typography
                        variant="body2"
                        className="mt-2 font-medium text-[#11AC9E]"
                      >
                        {t('Click to upload')}
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        {t('or drag and drop')}
                      </Typography>
                      <Typography variant="caption" className="text-gray-400">
                        SVG, PNG, JPG, PDF, DOC or GIF (max. 800×400px)
                      </Typography>
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                    <div className="max-h-46 m-2 mb-0 overflow-auto pr-2">
                      {files.map((fileObj: any, index: any) => (
                        <Box
                          key={index}
                          display="flex"
                          flexDirection={'column'}
                          justifyContent="space-between"
                          mb={1}
                          p={1}
                          gap={2}
                          border="1px solid #ddd"
                          borderRadius="8px"
                          position={'relative'}
                        >
                          {/* File Icon and Info */}
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            flex="1"
                          >
                            {/* File Icon */}
                            {getFileIcon(fileObj.type)}
                            {/* File Details */}
                            <Box>
                              <Typography variant="body2" color="textPrimary">
                                {fileObj.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {getFileSize(fileObj.size)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Progress Bar */}
                          <Box width="100%">
                            <LinearProgress
                              variant="determinate"
                              value={fileObj.progress}
                              style={{ height: '6px', borderRadius: '4px' }}
                            />
                          </Box>

                          {/* Actions */}
                          <Tooltip
                            title="Delete"
                            className="absolute right-4 top-4"
                          >
                            <IconButton
                              size="small"
                              onClick={() => removeFile(index)}
                            >
                              <Delete fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </div>
                  </div>
                </FormControl>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex justify-between gap-2  border-t pt-4">
              <button
                type="button"
                className="text-base h-[44px] w-[220px] rounded-lg border border-[#D0D5DD] bg-[#fff] p-2 font-semibold  text-[#344054]"
                onClick={handleClose}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="text-base h-[44px] w-[220px] rounded-lg border border-[#91BEE7] bg-[#11AC9E] p-2 font-semibold text-[#fff]  hover:bg-[#90BEE6]"
                onClick={handleSave}
              >
                {t('Send')}
              </button>
            </div>
          </article>
        </section>
      )}
    </>
  );
};
